import React from 'react';
import { Link } from 'react-router-dom';

/**
 * BrandBookPage — the full Elevated brand book, rendered inside the React
 * app. Route: /brand-book (public). Mirrors the PDF content so viewers
 * always get the current version regardless of static-file quirks in prod.
 */

const LOGO = 'https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png';

// Palette
const primaryPalette = [
  { name: 'Ink', hex: '#0F0B08', rgb: '15, 11, 8', hsl: '24 30% 5%', role: 'Full-bleed dark backgrounds, hero, video pages.' },
  { name: 'Bone', hex: '#F5EFE6', rgb: '245, 239, 230', hsl: '36 43% 93%', role: 'Body text on dark. Primary "white" — never use pure #fff.' },
  { name: 'Sand', hex: '#D9CCB8', rgb: '217, 204, 184', hsl: '36 33% 79%', role: 'Secondary text, sub-copy, less-important labels.' },
  { name: 'Saddle Gold', hex: '#C8A672', rgb: '200, 166, 114', hsl: '36 46% 62%', role: 'Primary accent. CTAs, links, LIVE dots, active states.' },
  { name: 'Warm Gold', hex: '#D9BD88', rgb: '217, 189, 136', hsl: '37 55% 69%', role: 'CTA hover state only.' },
  { name: 'Ember', hex: '#FF6B4A', rgb: '255, 107, 74', hsl: '11 100% 65%', role: 'LIVE indicator pulse, attention alerts.' },
];

const semanticPalette = [
  { name: 'Success', hex: '#4A7A5B', hsl: '144 20% 39%', role: 'Free Event badge, confirmations, "watching now" tiles.' },
  { name: 'Destructive', hex: '#C94A3A', hsl: '7 60% 51%', role: 'Cancel, delete, "Cancelled" column, End Event.' },
  { name: 'Warning', hex: '#D1A144', hsl: '41 61% 54%', role: 'Warnings, "no URL set" callouts, pending states.' },
  { name: 'Ink Card', hex: '#1A120B', hsl: '28 33% 8%', role: 'Card / surface background on dark pages.' },
];

const pillars = [
  { num: '01', title: 'Live Event Streams', copy: 'The events we film — team roping, barrel racing, steer riding, wild horses and more — streamed live to phone, desktop, or paired TV.' },
  { num: '02', title: 'Coach Critiques', copy: 'A real coach breaks down your run on video. Voice-over, lines drawn on your form, drill list.' },
  { num: '03', title: 'Event Photos & Video', copy: 'Every run we film gets tagged. Preview free, download the ones you want to keep.' },
  { num: '04', title: 'Training Library', copy: 'Free clinics, drill breakdowns, and community posts across disciplines.' },
];

const doList = [
  '"Every event. Live from the arena."',
  '"Then get your run broken down."',
  '"We film. We coach. We store."',
  '"One membership. Every stream."',
  '"Never miss your kid\'s run."',
  '"Coach breakdown · 6:41"',
];
const dontList = [
  '"The whole roping world. One place."',
  '"Unlock premium performance analytics."',
  '"AI-powered rodeo optimization."',
  '"Subscribe now for full access!"',
  '"Don\'t miss out on exclusive content!"',
  '"🎬 Watch amazing coach content"',
];

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.4em] text-[#c8a672] font-medium mb-4">{children}</div>
);
const H2 = ({ children }) => (
  <h2 className="font-display font-bold uppercase text-4xl sm:text-5xl leading-[0.95] tracking-tight mb-6">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="font-display font-bold uppercase text-xl text-[#c8a672] mt-10 mb-3">{children}</h3>
);
const P = ({ children }) => <p className="text-[#d9ccb8] leading-relaxed mb-4 max-w-[65ch]">{children}</p>;
const Quote = ({ children, accent = '#c8a672' }) => (
  <div className="border-l-4 pl-6 py-4 my-6 italic text-[#f5efe6] text-lg sm:text-xl bg-[#c8a672]/5" style={{ borderColor: accent, fontFamily: "'Libre Baskerville', serif" }}>
    {children}
  </div>
);
const Callout = ({ label, children }) => (
  <div className="rounded-lg border-2 border-[#c8a672] bg-[#c8a672]/8 p-5 my-6">
    <div className="font-display font-bold uppercase text-[#c8a672] tracking-widest text-sm mb-2">{label}</div>
    <div className="text-[#d9ccb8]">{children}</div>
  </div>
);

const Section = ({ eyebrow, title, children }) => (
  <section className="max-w-3xl mx-auto py-16 border-b border-[#c8a672]/15 last:border-b-0">
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    {title && <H2>{title}</H2>}
    {children}
  </section>
);

const SwatchCard = ({ s }) => (
  <div className="rounded-xl border border-[#c8a672]/20 overflow-hidden">
    <div className="h-28 flex items-end p-4" style={{ background: s.hex }}>
      <span className="font-mono text-xs" style={{ color: parseInt(s.hex.slice(1), 16) > 0x888888 ? '#0f0b08' : '#f5efe6' }}>
        {s.hex}
      </span>
    </div>
    <div className="p-4 bg-[#1a120b]/60">
      <div className="font-display font-bold uppercase text-lg">{s.name}</div>
      <div className="font-mono text-xs text-[#c8a672] mt-1">
        {s.hex}{s.rgb ? ` · rgb(${s.rgb})` : ''} · hsl({s.hsl})
      </div>
      <div className="text-xs text-[#d9ccb8] mt-2">{s.role}</div>
    </div>
  </div>
);

const BrandBookPage = () => (
  <div className="min-h-screen bg-[#0f0b08] text-[#f5efe6] px-6" style={{ fontFamily: 'Inter, sans-serif' }}>
    {/* Cover */}
    <section className="max-w-3xl mx-auto min-h-[80vh] flex flex-col justify-center py-24 border-b border-[#c8a672]/15">
      <img src={LOGO} alt="Elevated" className="h-24 w-auto self-start mb-10" />
      <Eyebrow>Brand Book · Version 1.0 · Feb 2026</Eyebrow>
      <h1 className="font-display font-bold uppercase text-6xl sm:text-8xl leading-[0.9] tracking-tight mb-8">
        Every event.<br />
        <span className="italic font-light text-[#c8a672]">Live from the arena.</span>
      </h1>
      <p className="text-lg sm:text-xl text-[#d9ccb8] italic max-w-2xl" style={{ fontFamily: "'Libre Baskerville', serif" }}>
        A private membership for the rodeo community — live event streams,
        critiques from real coaches, photos &amp; videos of every ride, and
        a training library, all under one roof.
      </p>
      <div className="mt-16">
        <Link to="/brand-kit" className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#c8a672] hover:underline">
          ← Back to Brand Kit
        </Link>
      </div>
    </section>

    {/* Who we are */}
    <Section eyebrow="01 · Who We Are" title="What Elevated is.">
      <P>Elevated Roping Media is a private membership platform for the rodeo community. We film the events, we coach the runs, we store the photos — all under one roof.</P>
      <Quote>A private membership for the rodeo community — live event streams, critiques from real coaches, photos &amp; videos of your rides, and a training library.</Quote>
      <P>
        <strong className="text-[#f5efe6]">We are not</strong> a social feed.
        We are not just a video library. We are the place where the run
        happens, the run gets studied, and the run gets kept forever.
      </P>
      <H3>The Four Pillars</H3>
      {pillars.map((p) => (
        <div key={p.num} className="grid grid-cols-[60px_1fr] gap-6 py-6 border-b border-[#c8a672]/15">
          <div className="font-display font-bold text-5xl text-[#c8a672] leading-none">{p.num}</div>
          <div>
            <div className="font-display font-bold uppercase text-2xl mb-2">{p.title}</div>
            <div className="text-[#d9ccb8]">{p.copy}</div>
          </div>
        </div>
      ))}
      <P><strong className="text-[#f5efe6]">Order matters.</strong> Live Streams is #1 — that is what people show up for. Everything else supports it.</P>
    </Section>

    {/* Audience */}
    <Section eyebrow="02 · Audience & Coverage" title="Who this is for.">
      <H3>Primary audience</H3>
      <P>Working ropers, barrel racers, steer riders, wild-horse competitors, and their families. Alberta / Western Canada first, but the product works anywhere rodeo does.</P>
      <H3>Secondary</H3>
      <P>Coaches, event organizers, sponsors, camera operators.</P>
      <H3>Not our audience</H3>
      <P>Casual sports fans, non-rodeo influencers, generic fitness / lifestyle followers.</P>
      <Callout label="The Tone Check">
        If it wouldn't work said out loud in a warm-up pen at Innisfail, it doesn't belong on the site.
      </Callout>
      <H3>Disciplines we cover</H3>
      <div className="font-display uppercase tracking-widest text-lg sm:text-xl leading-relaxed text-[#f5efe6] my-4">
        Team Roping · Barrel Racing · Steer Riding · Wild Horses ·<br />
        Breakaway · Tie-Down · Clinics · Futurities
      </div>
      <P><strong className="text-[#f5efe6]">Call events events</strong>, not "ropings". Roping is one discipline in the mix.</P>
    </Section>

    {/* Logo */}
    <Section eyebrow="03 · Logo" title="The mark.">
      <P>The Elevated Roping Media mark is a stacked "EM" monogram with the full wordmark set to the right in condensed uppercase display type.</P>
      <div className="p-10 border border-[#c8a672]/20 rounded-xl flex items-center justify-center my-4 bg-[#0f0b08]">
        <img src={LOGO} alt="Elevated on dark" className="max-h-24" />
      </div>
      <div className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#c8a672] mb-8">Primary use · Ink background</div>
      <div className="p-10 border border-[#c8a672]/20 rounded-xl flex items-center justify-center my-4 bg-[#f5efe6]">
        <img src={LOGO} alt="Elevated on light" className="max-h-24" />
      </div>
      <div className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#c8a672] mb-8">Secondary use · Bone background</div>
      <H3>Rules</H3>
      <ul className="space-y-2 text-[#d9ccb8]">
        <li>→ Minimum clear space around the logo = height of the "E" letterform.</li>
        <li>→ Minimum display size = 80px tall on screen, 15mm tall in print.</li>
        <li>→ Never re-color the monogram outside the Saddle Gold system.</li>
        <li>→ Never stretch, tilt, add drop-shadow, or apply gradient overlays.</li>
        <li>→ Never place the logo over a busy image without a solid backdrop.</li>
      </ul>
    </Section>

    {/* Colors */}
    <Section eyebrow="04 · Color" title="The palette.">
      <P>Dark-first. Solid backgrounds, never gradients-on-white. Gold is the only accent color for CTAs and links.</P>
      <H3>Primary palette</H3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
        {primaryPalette.map((s) => <SwatchCard key={s.hex} s={s} />)}
      </div>
      <H3>Semantic (status) palette</H3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
        {semanticPalette.map((s) => <SwatchCard key={s.hex} s={s} />)}
      </div>
      <Callout label="Rules">
        Backgrounds are solid dark, never gradients-on-white · Gold is the only accent color for CTAs and links · Semantic colors only for status, not decoration · Never use pure #000 or #fff — use Ink and Bone.
      </Callout>
    </Section>

    {/* Typography */}
    <Section eyebrow="05 · Typography" title="Voice, set in type.">
      <div className="border-l-2 border-[#c8a672] pl-6 my-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c8a672] mb-3">Display · Barlow Condensed 700</div>
        <div className="font-display font-bold uppercase text-6xl leading-[0.9]">
          Every event.<br /><span className="italic font-light">Live from the arena.</span>
        </div>
      </div>
      <div className="border-l-2 border-[#c8a672] pl-6 my-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c8a672] mb-3">Eyebrow · Inter 500 UPPER · 0.4em tracking · Saddle Gold</div>
        <div className="text-[11px] uppercase tracking-[0.4em] text-[#c8a672]">Live · Team Roping</div>
      </div>
      <div className="border-l-2 border-[#c8a672] pl-6 my-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c8a672] mb-3">Body · Inter 400</div>
        <p className="text-[#f5efe6]">Team roping. Barrel racing. Steer riding. Wild horses. Watch every run live on your phone, your couch, or paired to your TV.</p>
      </div>
      <div className="border-l-2 border-[#c8a672] pl-6 my-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c8a672] mb-3">Serif accent · Libre Baskerville Italic</div>
        <div className="italic text-xl text-[#f5efe6]" style={{ fontFamily: "'Libre Baskerville', serif" }}>"If it's on the arena floor, it's on your screen."</div>
      </div>
      <div className="border-l-2 border-[#c8a672] pl-6 my-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#c8a672] mb-3">Mono · timestamps &amp; labels</div>
        <div className="font-mono text-xs uppercase tracking-[0.4em] text-[#c8a672]">REC · 01 / 04 · Coach Breakdown · 6:41</div>
      </div>
    </Section>

    {/* Voice */}
    <Section eyebrow="06 · Voice & Tone" title="How we sound.">
      <ul className="space-y-2 text-[#d9ccb8] mb-8">
        <li>→ <strong className="text-[#f5efe6]">Cowboy-real, not cowboy-costume.</strong> No "yeehaw", no "wrangle".</li>
        <li>→ <strong className="text-[#f5efe6]">Short sentences. Working verbs.</strong> "We film. We coach. We store."</li>
        <li>→ <strong className="text-[#f5efe6]">Confident, never braggy.</strong> Show the product; don't oversell it.</li>
        <li>→ <strong className="text-[#f5efe6]">Dry humour is welcome.</strong> Never sarcastic, never corporate.</li>
        <li>→ <strong className="text-[#f5efe6]">Family-safe.</strong> Every line readable to Grandma.</li>
      </ul>
      <H3>Do / Don't</H3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
        <div className="p-5 rounded-lg border border-[#4a7a5b]/40 bg-[#4a7a5b]/10">
          <div className="font-display font-bold uppercase text-[#4a7a5b] mb-3">Do</div>
          <ul className="space-y-2 text-[#d9ccb8] text-sm">
            {doList.map((x) => <li key={x}>{x}</li>)}
          </ul>
        </div>
        <div className="p-5 rounded-lg border border-[#c94a3a]/40 bg-[#c94a3a]/10">
          <div className="font-display font-bold uppercase text-[#c94a3a] mb-3">Don't</div>
          <ul className="space-y-2 text-[#d9ccb8] text-sm">
            {dontList.map((x) => <li key={x}>{x}</li>)}
          </ul>
        </div>
      </div>
      <H3>Words to avoid</H3>
      <P>Yeehaw · rodeo warrior · cowboy up · game-changer · leverage · unlock · premium · exclusive · optimize · synergy · empower · elevate <em className="text-[#c8a672]">(yes, even though it's in our name — the word does the work; we don't need to say it).</em></P>
    </Section>

    {/* Copy templates */}
    <Section eyebrow="07 · Copy Templates" title="Templates ready to ship.">
      <H3>Live event (paywall on)</H3>
      <Quote>
        <div className="not-italic font-sans text-xs uppercase tracking-[0.4em] text-[#ff6b4a] mb-2">LIVE · Team Roping</div>
        Innisfail Jackpot — Round 2<br />
        <span className="not-italic font-sans text-sm text-[#d9ccb8]">[ Watch Live ] · Subscribers only</span>
      </Quote>
      <H3>Live event (free / sponsored)</H3>
      <Quote accent="#4a7a5b">
        <div className="not-italic font-sans text-xs uppercase tracking-[0.4em] text-[#4a7a5b] mb-2">LIVE · Free Event</div>
        Innisfail Jackpot — Sponsored by [Sponsor]<br />
        <span className="not-italic font-sans text-sm text-[#d9ccb8]">[ Watch Now ] · No login required</span>
      </Quote>
      <H3>Post-event "your photos ready"</H3>
      <Quote>
        <span className="not-italic font-sans">Your run at [Event Name] is up.<br />
        <strong className="text-[#f5efe6]">12 photos · 3 clips</strong> tagged to you.<br />
        <span className="text-sm text-[#d9ccb8]">[ Open Gallery ]</span></span>
      </Quote>
      <H3>Membership pitch</H3>
      <Quote>
        <div className="not-italic font-display font-bold uppercase text-3xl text-[#f5efe6] mb-2">$25 a month.</div>
        <span className="not-italic font-sans">Every stream. Every photo of you. Every coach breakdown you buy.<br />
        <span className="text-sm text-[#d9ccb8]">[ Start Membership ]</span></span>
      </Quote>
      <H3>Critique response (SMS)</H3>
      <Quote>
        <span className="not-italic font-sans">Your critique from Coach [Name] is ready.<br />
        <span className="text-sm text-[#d9ccb8]">[link] · Watch anytime · From Elevated</span></span>
      </Quote>
      <H3>Sponsor callout</H3>
      <Quote>
        <span className="not-italic font-sans"><strong className="text-[#f5efe6]">[Sponsor Name] is putting this run on us.</strong><br />
        Watch live free — on any screen — courtesy of [Sponsor Name].</span>
      </Quote>
    </Section>

    {/* Principles */}
    <Section eyebrow="08 · Product Principles" title="How we build.">
      <ul className="space-y-3 text-[#d9ccb8]">
        <li>→ <strong className="text-[#f5efe6]">Phone-first.</strong> Every screen has to work with a gloved thumb in a parking lot.</li>
        <li>→ <strong className="text-[#f5efe6]">One thing per screen.</strong> If a page tries to sell two things, it sells zero.</li>
        <li>→ <strong className="text-[#f5efe6]">Membership &gt; pay-per-view.</strong> One price, one wall.</li>
        <li>→ <strong className="text-[#f5efe6]">Watch anywhere.</strong> Phone → desktop → TV. If it plays in one and not the other, it's broken.</li>
        <li>→ <strong className="text-[#f5efe6]">Family-safe by default.</strong> Nothing we couldn't show a kid.</li>
        <li>→ <strong className="text-[#f5efe6]">Every viewer counts.</strong> Free event? We still track anonymous view counts for the sponsor.</li>
      </ul>
    </Section>

    {/* Guardrails */}
    <Section eyebrow="09 · What Elevated Is Not" title="Guardrails.">
      <ul className="space-y-2 text-[#d9ccb8] mb-8">
        <li>→ Not a TikTok / Instagram substitute.</li>
        <li>→ Not a general sports platform — every pixel is rodeo.</li>
        <li>→ Not an AI-powered anything. A real coach is in the loop.</li>
        <li>→ Not a "content locker" — you can preview a lot for free.</li>
        <li>→ Not a "cowboy-themed lifestyle brand" merch shop. This is a working tool.</li>
      </ul>
      <Callout label="When in doubt">
        Would a working roper — someone who was on a horse before breakfast — read this line and nod, or roll their eyes? If it's the second one, rewrite it.
      </Callout>
      <p className="mt-16 text-center font-mono text-[10px] uppercase tracking-[0.4em] text-[#c8a672]/60">
        Elevated Roping Media · Brand Book v1.0 · Feb 2026
      </p>
      <div className="text-center mt-4">
        <Link to="/brand-kit" className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#c8a672] hover:underline">
          ← Back to Brand Kit
        </Link>
      </div>
    </Section>
  </div>
);

export default BrandBookPage;
